import React, { useState } from "react";
import {
	Button,
	Text,
	Divider,
	makeStyles,
	tokens,
	Spinner,
	Switch,
} from "@fluentui/react-components";
import {
	ArrowLeft24Regular,
	PersonAccounts24Regular,
	Payment24Regular,
	Delete24Regular,
	Mail24Regular,
	Eye24Regular,
} from "@fluentui/react-icons";
import { useAuth } from "../context/BaseAuthProvider";
import { useSubscription } from "../context/SubscriptionContext";
import { httpClient } from "../lib/requestFunctions";
import DeleteAccountModal from "./DeleteAccountModal";
import PaywallModal from "./PaywallModal";

const useStyles = makeStyles({
	container: {
		padding: tokens.spacingHorizontalM,
	},
	backButton: {
		marginBottom: tokens.spacingVerticalM,
	},
	section: {
		marginBottom: tokens.spacingVerticalL,
	},
	sectionTitle: {
		display: "flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalS,
		marginBottom: tokens.spacingVerticalS,
	},
	infoRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		paddingTop: tokens.spacingVerticalXS,
		paddingBottom: tokens.spacingVerticalXS,
	},
	infoLabel: {
		color: tokens.colorNeutralForeground3,
	},
	statusBadge: {
		display: "inline-flex",
		alignItems: "center",
		paddingTop: tokens.spacingVerticalXXS,
		paddingBottom: tokens.spacingVerticalXXS,
		paddingLeft: tokens.spacingHorizontalS,
		paddingRight: tokens.spacingHorizontalS,
		borderRadius: tokens.borderRadiusCircular,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightSemibold,
	},
	statusActive: {
		backgroundColor: tokens.colorPaletteGreenBackground1,
		color: tokens.colorPaletteGreenForeground1,
	},
	statusTrial: {
		backgroundColor: tokens.colorPaletteYellowBackground1,
		color: tokens.colorPaletteYellowForeground2,
	},
	statusExpired: {
		backgroundColor: tokens.colorPaletteRedBackground1,
		color: tokens.colorPaletteRedForeground1,
	},
	statusCancelled: {
		backgroundColor: tokens.colorNeutralBackground3,
		color: tokens.colorNeutralForeground3,
	},
	contactSection: {
		marginBottom: tokens.spacingVerticalL,
	},
	contactLink: {
		color: tokens.colorBrandForeground1,
		textDecorationLine: "none",
		":hover": {
			textDecorationLine: "underline",
		},
	},
	dangerSection: {
		marginTop: tokens.spacingVerticalL,
		display: "flex",
		flexDirection: "column",
	},
	dangerTitle: {
		color: tokens.colorPaletteRedForeground1,
	},
	divider: {
		marginBottom: "0.5rem",
	},
	manageButton: {
		marginTop: tokens.spacingVerticalS,
	},
	versionFooter: {
		marginTop: tokens.spacingVerticalL,
		textAlign: "center",
		color: tokens.colorNeutralForeground4,
	},
});

// Injected at build time (webpack DefinePlugin / Vite define). Falls back to
// "dev" when running unbuilt.
const APP_VERSION = process.env.APP_VERSION || "dev";

interface AccountSettingsProps {
	onBack: () => void;
}

export const AccountSettings: React.FC<AccountSettingsProps> = ({
	onBack,
}) => {
	const styles = useStyles();
	const { user, refreshUser, isAuthenticated, login } = useAuth();
	const {
		subscriptionStatus,
		trialDaysRemaining,
		isTrialActive,
		openCheckout,
		openCustomerPortal,
		portalInProgress,
	} = useSubscription();
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [showPaywall, setShowPaywall] = useState(false);
	const [guidanceLoading, setGuidanceLoading] = useState(false);

	const handleGuidanceToggle = async () => {
		if (!user) return;
		setGuidanceLoading(true);
		try {
			await httpClient.patch('/user/preferences', {
				guidanceEnabled: !user.guidanceEnabled,
			});
			await refreshUser();
		} finally {
			setGuidanceLoading(false);
		}
	};

	if (!user) return null;

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-IE", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
	};

	const getStatusBadgeClass = () => {
		switch (subscriptionStatus) {
			case "active":
				return `${styles.statusBadge} ${styles.statusActive}`;
			case "trial":
				return `${styles.statusBadge} ${styles.statusTrial}`;
			case "expired":
			case "past_due":
				return `${styles.statusBadge} ${styles.statusExpired}`;
			case "cancelled":
				return `${styles.statusBadge} ${styles.statusCancelled}`;
			default:
				return styles.statusBadge;
		}
	};

	const getStatusLabel = () => {
		switch (subscriptionStatus) {
			case "active":
				return "Active";
			case "trial":
				return isTrialActive
					? `Trial (${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} left)`
					: "Trial Expired";
			case "expired":
				return "Expired";
			case "past_due":
				return "Past Due";
			case "cancelled":
				return "Cancelled";
			default:
				return subscriptionStatus;
		}
	};

	return (
		<div className={styles.container}>
			<Button
				className={styles.backButton}
				appearance="subtle"
				icon={<ArrowLeft24Regular />}
				onClick={onBack}
			>
				Back
			</Button>

			{/* Account Information */}
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<PersonAccounts24Regular />
					<Text weight="semibold" size={400}>
						Account Information
					</Text>
				</div>
				<div className={styles.infoRow}>
					<Text className={styles.infoLabel} size={300}>
						Name
					</Text>
					<Text size={300}>{user.name}</Text>
				</div>
				<div className={styles.infoRow}>
					<Text className={styles.infoLabel} size={300}>
						Email
					</Text>
					<Text size={300}>{user.email}</Text>
				</div>
				<div className={styles.infoRow}>
					<Text className={styles.infoLabel} size={300}>
						Sign-in
					</Text>
					<Text size={300}>
						{user.provider === "microsoft"
							? "Microsoft"
							: "Google"}
					</Text>
				</div>
				<div className={styles.infoRow}>
					<Text className={styles.infoLabel} size={300}>
						Member since
					</Text>
					<Text size={300}>
						{formatDate(user.createdAt)}
					</Text>
				</div>
			</div>

			<Divider className={styles.divider} />

			{/* Subscription */}
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<Payment24Regular />
					<Text weight="semibold" size={400}>
						Subscription
					</Text>
				</div>
				<div className={styles.infoRow}>
					<Text className={styles.infoLabel} size={300}>
						Status
					</Text>
					<span className={getStatusBadgeClass()}>
						{getStatusLabel()}
					</span>
				</div>
				{user.subscriptionPeriodEnd &&
					subscriptionStatus === "active" && (
						<div className={styles.infoRow}>
							<Text
								className={styles.infoLabel}
								size={300}
							>
								{user.cancelAtPeriodEnd
									? "Expires on"
									: "Renews on"}
							</Text>
							<Text size={300}>
								{formatDate(
									user.subscriptionPeriodEnd,
								)}
							</Text>
						</div>
					)}
				{subscriptionStatus === "active" ? (
					<Button
						className={styles.manageButton}
						appearance="secondary"
						onClick={openCustomerPortal}
						disabled={portalInProgress}
						icon={
							portalInProgress ? (
								<Spinner size="tiny" />
							) : undefined
						}
					>
						{portalInProgress
							? "Opening..."
							: "Manage Subscription"}
					</Button>
				) : (
					<Button
						className={styles.manageButton}
						appearance="primary"
						onClick={() => setShowPaywall(true)}
					>
						Subscribe
					</Button>
				)}
			</div>

			<Divider className={styles.divider} />

			{/* Contact / Support */}
			<div className={styles.contactSection}>
				<div className={styles.sectionTitle}>
					<Mail24Regular />
					<Text weight="semibold" size={400}>
						Need Help?
					</Text>
				</div>
				<Text size={300}>
					Get in touch at{" "}
					<a
						href="mailto:contact@cuntasmitool.ie"
						className={styles.contactLink}
					>
						contact@cuntasmitool.ie
					</a>
				</Text>
			</div>

			<Divider className={styles.divider} />

			{/* Preferences */}
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<Eye24Regular />
					<Text weight="semibold" size={400}>
						Preferences
					</Text>
				</div>
				<div className={styles.infoRow}>
					<Text className={styles.infoLabel} size={300}>
						Getting started guidance
					</Text>
					<Switch
						checked={user?.guidanceEnabled ?? false}
						onChange={handleGuidanceToggle}
						disabled={guidanceLoading}
					/>
				</div>
				<Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
					Shows step-by-step hints as you use the curriculum browser.
				</Text>
			</div>

			<Divider className={styles.divider} />

			{/* Danger Zone */}
			<div className={styles.dangerSection}>
				<div className={styles.sectionTitle}>
					<Delete24Regular />
					<Text
						weight="semibold"
						size={400}
						className={styles.dangerTitle}
					>
						Danger Zone
					</Text>
				</div>
				<Text size={200}>
					Permanently delete your account and all associated
					data. This action cannot be undone.
				</Text>
				<Button
					className={styles.manageButton}
					appearance="outline"
					onClick={() => setDeleteModalOpen(true)}
				>
					Delete Account
				</Button>
			</div>

			<DeleteAccountModal
				isOpen={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
			/>

			<PaywallModal
				isOpen={showPaywall}
				isAuthenticated={isAuthenticated}
				onClose={() => setShowPaywall(false)}
				onSubscribe={(tier) => {
					setShowPaywall(false);
					openCheckout(tier);
				}}
				onLogin={() => login("microsoft")}
				onLoginGoogle={() => login("google")}
			/>

			<Text as="p" size={200} className={styles.versionFooter}>
				Cuntas MíTool v{APP_VERSION}
			</Text>
		</div>
	);
};

export default AccountSettings;
